import { useState } from 'react'
import { Card, CardLink } from './Card'
import { StarHistory } from './StarHistory'
import { StarEarningsDialog } from './StarEarnings'
import { daysLeftInPeriod, type MyStars, type StarBucket } from '../lib/stars'
import { useHubLang } from '../lib/i18n'
import { HOME, STARS } from '../lib/hubCopy'

/**
 * Star One on the engagement hub rail — TWO cards, not one (David, 14 Sep:
 * "a widget for stars you gave with an action for Send Stars and stars you
 * received with Redeem Stars"). REC-8, REC-22, SHL-13.
 *
 * ── WHY TWO CARDS IS THE RIGHT SHAPE AND NOT JUST A LAYOUT PREFERENCE ───────
 *
 * The plan's hardest rule is that there are TWO CURRENCIES and collapsing them
 * lets somebody buy a jacket with their giving allowance. One card showed both
 * numbers side by side under one heading with one link, which is the closest
 * this app ever came to implying they were the same pot. Two cards, two verbs,
 * two destinations: give goes to the wall, spend goes to the Store. **There is
 * still no total anywhere across the two, and a rail card is exactly where
 * somebody would be tempted to save space by summing them.** (The received card
 * does carry a twelve-month sum of what you EARNED — recognitions and activity,
 * 106 — which is one currency summed over time, a different thing from adding
 * the two pots together.)
 *
 * ── "RECOGNIZE", NOT "THANK" (David, 15 Sep) ────────────────────────────────
 *
 * The verb changed everywhere in one commit. A shout-out is a recognition with
 * ZERO stars on it — one row, by design (061) — and `STAR_PRESETS` opens with 0
 * precisely because that is a first-class thing to send. "Thank a colleague"
 * described the commonest case and quietly mislabelled the feature: the button
 * for safety, quality and One ConeTec is not a thank-you button. It is also why
 * the label is not "Send stars to a colleague", which David's first wording
 * suggested and his own correction rules out — you do not always send stars.
 *
 * ── "RECOGNIZE A COLLEAGUE" OPENS THE GIVE DIALOG HERE (David, 25 Sep) ─────
 *
 * This REVERSES his 15 Sep call, when he kept the button as a link to #/stars
 * because opening a form here would be "a second give surface" — a second home
 * for the allowance cap, the custom-amount clamp, the fifteen-minute edit
 * window and the celebration. Chandra reported the link as a bug: it took her
 * to Star One, where she had to press "Recognize a colleague" AGAIN.
 *
 * The old reason does not survive, and it is worth saying why rather than just
 * deleting it: the thing it feared was a SECOND FORM, and there is not one.
 * `GiveForm` is one component, and the hub renders that same component in the
 * same `<Dialog>` Star One uses (REC-20). Everything the 15 Sep comment named
 * lives inside GiveForm, so it cannot drift between the two pages. DO NOT copy
 * GiveForm's fields into this card; open the one form or do not open one.
 *
 * So the button calls `onGive` when the route passes one, and stays a LINK when
 * it does not — a render with no route behind it still goes somewhere true.
 * The `CardLink` in the header is SHL-13's standing rule (a widget with a
 * screen behind it always links to it) and is unchanged.
 *
 * ── STILL NO RANKING (D10, upheld by B22) ──────────────────────────────────
 *
 * Both feeds are the reader's own, in the order `recognition_wall(scope)`
 * returned them — newest first, no sort argument to pass. "Your biggest this
 * month" is a league table with one row, and a widget is where it would be
 * least noticed. The chart compares you to your own past and to nobody else's.
 */
export function StarsGivenWidget({
  stars,
  onGive,
}: {
  stars: MyStars | null
  /** Open the route's give dialog. Absent → the button is a link to #/stars. */
  onGive?: () => void
}) {
  const h = HOME[useHubLang()]
  const days = daysLeftInPeriod()
  const spent = stars?.allowanceSpent ?? 0

  return (
    <Card title={h.starsYouGave} action={<CardLink to="#/stars" label="Star One" />}>
      <div className="starwidget__nums">
        <div>
          <p className="starwidget__num">
            ★ {stars ? stars.allowanceRemaining.toLocaleString() : '—'}
          </p>
          <p className="starwidget__label">
            {h.leftToGive(days)}
          </p>
        </div>
        <div>
          <p className="starwidget__num">★ {stars ? spent.toLocaleString() : '—'}</p>
          <p className="starwidget__label">{h.givenThisMonth}</p>
        </div>
      </div>

      {/* THE LIST OF WHO YOU RECOGNIZED LEFT THE RAIL (David, 11 Oct: "the
          widget is just the hook … the details can be in the modal or the
          link"). The ones you sent are on #/stars, behind the header link;
          the card keeps the two numbers and the one action. */}

      {/* THE EXPLANATORY NOTE IS GONE. David, 20 Sep.

          It said "Your allowance to give resets every month. It cannot be
          redeemed." Both facts are true and both are still enforced — the
          allowance is `star_period`'s grant and the balance is a different
          column, which is what stops somebody buying a jacket with stars they
          were given to give away. What went is the SENTENCE, not the rule.

          The card already carries the distinction where it counts: two cards,
          two headings, two verbs, and the number itself is labelled. A
          paragraph of policy under a widget is the kind of thing people stop
          reading in week one, and it was competing with the one control that
          matters. Do not restore it as a caption; if the distinction ever
          turns out not to land, that is a question about the two headings. */}

      {/* The route's give dialog, or a link when there is no route behind
          this render. Never a form of its own — see the header. */}
      {onGive ? (
        <button type="button" className="btn btn--primary btn--sm btn--block" aria-haspopup="dialog" onClick={onGive}>
          {h.recognizeColleague}
        </button>
      ) : (
        <a className="btn btn--primary btn--sm btn--block" href="#/stars">
          {h.recognizeColleague}
        </a>
      )}
    </Card>
  )
}

export function StarsReceivedWidget({
  stars,
  series = [],
  locale = 'en-CA',
}: {
  stars: MyStars | null
  /** Twelve months from my_star_series('12m'). All zeros renders no chart. */
  series?: StarBucket[]
  locale?: string
}) {
  const lang = useHubLang()
  const h = HOME[lang]
  const held = stars?.balanceHeld ?? 0
  const [history, setHistory] = useState(false)

  return (
    <Card
      title={h.starsYouReceived}
      action={<CardLink to="#/stars/store" label={h.store} />}
    >
      <div className="starwidget__nums">
        <div>
          <p className="starwidget__num">
            ★ {stars ? stars.balanceAvailable.toLocaleString() : '—'}
          </p>
          <p className="starwidget__label">{h.toSpend}</p>
        </div>
        {/* HELD is a third number and is shown only when there is one (063).
            A recognition given in the last fifteen minutes is not yours yet,
            and a balance that silently excluded it would look like a mistake. */}
        {held > 0 && (
          <div>
            <p className="starwidget__num">★ {held.toLocaleString()}</p>
            <p className="starwidget__label">{h.arrivingShortly}</p>
          </div>
        )}
      </div>

      {/* Renders nothing for an all-zero year. Since 106 it counts every
          credit, so after first sign-in nobody's year is empty — the welcome's
          1,000 is in it, by David's call (24 Sep). */}
      <StarHistory buckets={series} range="12m" locale={locale} />

      {/* The last three credits left the card on 11 Oct (David: the widget is
          the hook, the details are in the modal). The modal is the one this
          card has opened since REC-57; the chart above is its picture. "See
          all" is a BUTTON, not a link: there is no screen behind it, only
          the dialog. Redeem keeps its full words for a screen reader. */}
      <div className="hook__row">
        <a className="btn btn--primary btn--sm" href="#/stars/store" aria-label={h.redeem}>
          {h.redeemShort}
        </a>
        <button type="button" className="btn btn--quiet btn--sm" aria-haspopup="dialog" onClick={() => setHistory(true)}>
          {STARS[lang].seeAllEarned}
        </button>
      </div>
      <StarEarningsDialog open={history} onClose={() => setHistory(false)} locale={locale} />
    </Card>
  )
}
